import CompanyName from "components/Companies/CompanyName";
import TierBadge from "components/Companies/TierBadge";
import { companyNameExportValue } from "utilities/companyName";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanies } from "hooks/crm/useCompanies";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal, isPartner } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import SearchPanel from "components/SearchPanel";
import CreateCompanyMenu from "components/Companies/CreateCompanyMenu";
import CreateCompanyModal from "components/Companies/CreateCompanyModal";
import { CREATE_TYPES, createSuccessPath } from "utilities/companyCreate";
import InviteClientModal from "components/Companies/InviteClientModal";
import PartnerCompaniesHeader from "components/Companies/PartnerCompaniesHeader";
import RegistrationStatusPill from "components/Registration/RegistrationStatusPill";
import RecordTable from "components/Records/RecordTable";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import PageKpis from "components/PageKpis";
import PageHeader from "components/PageLayout";
import {
  Filter,
  FileText,
  Building2,
  CheckCircle,
  BarChart3,
  Plus,
  Network,
  AlertCircle,
} from "lucide-react";

const COMPANY_FILTER_FIELDS = filterFieldsFor("companies");

/**
 * Source is an object on the row ({ type, partners }) rather than a string,
 * because a company can be attributed to more than one partner.
 */
function SourceCell({ source }) {
  if (!source || !source.type) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (source.type === "self-serve") return <>Self-serve</>;
  if (source.type === "internal") return <>Internal</>;
  if (source.type === "partner") {
    const partners = Array.isArray(source.partners) ? source.partners : [];
    if (partners.length === 0) return <>Partner</>;
    return <>{partners.map((p) => p.partnerName).filter(Boolean).join(", ")}</>;
  }
  return <>{source.type}</>;
}

function sourceText(source) {
  if (!source?.type) return "";
  if (source.type === "partner") {
    const names = (source.partners || []).map((p) => p.partnerName).filter(Boolean);
    return names.length ? names.join(", ") : "Partner";
  }
  return source.type;
}

/** Newest of updatedAt / createdAt — a company with no edits still has a date. */
function lastTouched(company) {
  return [company.updatedAt, company.createdAt]
    .filter(Boolean)
    .reduce((best, v) => {
      const t = new Date(v).getTime();
      if (Number.isNaN(t)) return best;
      return best == null || t > best ? t : best;
    }, null);
}

function CompaniesInner() {
  const { filters, set } = useSearch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-updatedAt");
  const [filtersOpen, setFiltersOpen] = useState(false);
  // SOUND-296: one Create control, one modal. `createType` is the open modal's
  // type ("client" | "parent" | "partner") or null. Which types a user may
  // pick is decided in utilities/companyCreate, not here.
  const [createType, setCreateType] = useState(null);
  // SOUND-293: Invite Client flow moved here from the retired PartnerDashboard.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const auth = useAuth();
  const internal = isInternal(auth?.user);
  const partnerUser = isPartner(auth?.user);
  const effectiveCompanyId = useEffectiveCompanyId();

  const { companies, pagination, loading, error, refetch } = useCompanies({
    filters,
    page,
    limit,
    sort,
  });

  // Reset page when filters change (via SearchPanel apply)
  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    if (!effectiveCompanyId) return;
    makeApiRequest(
      "get",
      `${apiRoutes.getCompanies}/stats`,
      { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
      effectiveCompanyId
    )
      .then((res) => setStats(res?.data || null))
      .catch(() => setStats(null));
  }, [effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Counted against the declared defaults in searchConfig, which also disposes
  // of the `partnerId__label` side-channel keys the old check had to special-case
  // — they are not declared fields, so they cannot be mistaken for filters.
  const activeFilterCount = countActiveFilters(filters, COMPANY_FILTER_FIELDS);

  // SOUND-295: childCount, completenessSummary and midCount are embedded on each
  // row by GET /companies?include=summary (one aggregation) — the two per-row
  // N+1 fan-outs that used to live here are gone.
  //
  // Those three are produced by $lookups that run AFTER the $sort, so none of
  // them can carry a sortKey. Same rule as Client/DBA/Source elsewhere: no
  // affordance unless the server can actually order by it.
  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        width: internal ? "24%" : "30%",
        sortKey: "name",
        // SOUND-630: exports stay EMPTY for unnamed companies — writing
        // "New Merchant" into a spreadsheet would let it be read back as a
        // real business name.
        exportValue: companyNameExportValue,
        cell: (c) => {
          const completeness = c.completenessSummary;
          const appStatus = completeness?.status || null;
          const missing = completeness
            ? Math.max(0, (completeness.total || 0) - (completeness.filled || 0))
            : 0;
          const isDraftWithMissing =
            ["new", "in_progress"].includes(appStatus) && missing > 0;
          const childCount = typeof c.childCount === "number" ? c.childCount : 0;
          return (
            <div className="flex items-center gap-2">
              <CompanyName company={c} className="truncate font-medium" />
              {childCount > 0 && (
                <span
                  className="badge badge-brand shrink-0"
                  title={`Parent of ${childCount} ${childCount === 1 ? "company" : "companies"}`}
                >
                  <Network className="h-3 w-3" />
                  {childCount}
                </span>
              )}
              {isDraftWithMissing && (
                <span
                  className="badge badge-warning shrink-0"
                  title="Draft application is missing required fields"
                >
                  <AlertCircle className="h-3 w-3" />
                  {missing}
                </span>
              )}
            </div>
          );
        },
      },
      // Partners only ever see their own portfolio, so the client/partner
      // distinction is noise for them (SOUND-292).
      internal && {
        key: "companyType",
        header: "Type",
        width: "10%",
        sortKey: "companyType",
        className: "capitalize",
        cell: (c) => c.companyType || "—",
      },
      {
        key: "source",
        header: "Source",
        width: "18%",
        // Ordered by the STORED sourceType, so partner-attributed companies
        // group together even though the cell prints partner names.
        sortKey: "sourceType",
        exportValue: (c) => sourceText(c.source),
        cell: (c) => <SourceCell source={c.source} />,
      },
      {
        key: "applicationStatus",
        header: "App Status",
        width: "14%",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        exportValue: (c) => c.completenessSummary?.status || "",
        cell: (c) =>
          c.completenessSummary?.status ? (
            <RegistrationStatusPill status={c.completenessSummary.status} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "midCount",
        header: "MIDs",
        width: "5%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        exportValue: (c) => (typeof c.midCount === "number" ? c.midCount : ""),
        cell: (c) =>
          typeof c.midCount === "number" ? (
            <span className="badge badge-muted">{c.midCount}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      !partnerUser && {
        key: "tier",
        header: "Tier",
        width: "16%",
        sortKey: "tier",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        cell: (c) =>
          c.tier ? (
            <TierBadge tier={c.tier} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: "updatedAt",
        header: "Updated",
        width: "13%",
        sortKey: "updatedAt",
        defaultSortDir: "desc",
        headerClassName: "hidden xl:table-cell",
        className: "hidden xl:table-cell text-muted-foreground",
        exportValue: (c) => c.updatedAt || c.createdAt || "",
        cell: (c) => <RelativeTime value={lastTouched(c)} />,
      },
    ],
    [internal, partnerUser]
  );

  return (
    <div>
      <PageHeader
        title="Companies"
        subtitle="Manage registered companies."
        meta={[{ label: "Total", value: (pagination.total || 0).toLocaleString() }]}
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary" onClick={() => setFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="badge badge-brand ml-1">{activeFilterCount}</span>
              )}
            </button>
            {partnerUser && (
              // SOUND-293: Invite Client folded over from the retired
              // PartnerDashboard. A DIFFERENT VERB from Create — it sends an
              // invitation rather than creating a record — so it stays its own
              // button and is deliberately not folded into the Create menu.
              <button className="btn btn-secondary" onClick={() => setInviteOpen(true)}>
                <Plus className="h-4 w-4" />
                Invite Client
              </button>
            )}
            <CreateCompanyMenu onSelect={setCreateType} />
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        {/*
          SOUND-293: partner users see a 3-KPI strip (total clients / active /
          features enabled) folded in from the retired /dashboard. Internal users
          keep the registration-funnel KPIs.
        */}
        {partnerUser ? (
          <PartnerCompaniesHeader companies={companies} />
        ) : (
          <PageKpis
            items={[
              {
                label: "Total Registrations",
                value: stats?.totalRegistrations ?? 0,
                tone: "brand",
                loading: !stats,
                icon: <FileText />,
              },
              {
                label: "Drafts / In Progress",
                value: stats?.draftsInProgress ?? 0,
                tone: "warning",
                loading: !stats,
                icon: <FileText />,
              },
              {
                label: "Submitted",
                value: stats?.submittedRegistrations ?? 0,
                tone: "success",
                loading: !stats,
                icon: <CheckCircle />,
              },
              {
                label: "Submitted %",
                value:
                  stats && stats.totalRegistrations
                    ? (stats.submittedRegistrations || 0) / stats.totalRegistrations
                    : 0,
                format: "percent",
                tone: "info",
                loading: !stats,
                icon: <BarChart3 />,
              },
            ]}
          />
        )}

        <ListSearchBar
          value={filters.search || ""}
          onChange={(v) => set("search", v)}
          placeholder="Search company name"
        >
          {internal && (
            <select
              className="select w-auto"
              value={filters.companyType || ""}
              onChange={(e) => set("companyType", e.target.value)}
              aria-label="Type"
            >
              <option value="">Type: Any</option>
              <option value="client">Client</option>
              <option value="partner">Partner</option>
              <option value="processor">Processor</option>
            </select>
          )}
        </ListSearchBar>

        <RecordTable
          columns={columns}
          rows={companies}
          rowKey={(c) => c.companyId}
          onRowClick={(c) => navigate(`/companies/${c.companyId}`)}
          loading={loading}
          error={error}
          onRetry={refetch}
          filtered={activeFilterCount > 0}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          noMatch={{ icon: Building2, heading: "No matching companies" }}
          empty={{
            icon: Building2,
            heading: "No companies found",
            description: "No companies available.",
            errorText: "Failed to load companies.",
            // The empty state offers Client only — it is the action a user
            // staring at an empty company list actually wants, and the full
            // menu already lives in the header.
            action: (
              <button
                className="btn btn-primary"
                onClick={() => setCreateType(CREATE_TYPES.CLIENT)}
              >
                <Plus className="h-4 w-4" />
                Create Client
              </button>
            ),
          }}
        />

        <ListPagination
          page={pagination.page || page}
          totalPages={pagination.totalPages || 0}
          total={pagination.total || 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      <SearchPanel
        namespace="companies"
        title="Filter Companies"
        isExpanded={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        // Per SOUND-292:
        //   - Type is internal-only (partners don't need to distinguish client
        //     vs partner companies — they only see their own portfolio).
        //   - Account status is hidden for partners (their clients are always
        //     active from their perspective).
        //   - Tier is hidden for partners (internal concept).
        hiddenFields={partnerUser ? ["companyType", "status", "tier"] : []}
      />

      {createType && (
        // Keyed by type so switching from the menu remounts a clean form
        // rather than carrying a half-filled one across.
        <CreateCompanyModal
          key={createType}
          type={createType}
          onClose={() => setCreateType(null)}
          onCreated={(created) => {
            const path = createSuccessPath(createType, created?.companyId);
            setCreateType(null);
            refetch();
            // A client lands on the Registration tab so the user can finish
            // the form; a parent/partner has no registration and lands on the
            // record.
            if (path) navigate(path);
          }}
        />
      )}

      {inviteOpen && (
        <InviteClientModal onClose={() => setInviteOpen(false)} onInvited={() => refetch()} />
      )}
    </div>
  );
}

function Companies() {
  return (
    <SearchProvider namespace="companies">
      <CompaniesInner />
    </SearchProvider>
  );
}

export default requireAuth(Companies);
